import { AlertCircle, AlertTriangle, Check, ExternalLink } from 'lucide-react';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { Card, CardContent } from '@g17eco/ui-modern/components/ui/card';
import { cn } from '@g17eco/ui-modern/lib/utils';
import { type BulkImport } from '../../types/emissions';

interface BulkCompleteStepProps {
  importResult: BulkImport;
  onImportMore: () => void;
  onViewActivities: () => void;
  onViewFailedActivities?: () => void;
}

export const BulkCompleteStep = ({ importResult, onImportMore, onViewActivities, onViewFailedActivities }: BulkCompleteStepProps) => {
  const failedRows = importResult.rowResults.filter((r) => !r.success);
  const warningRows = importResult.rowResults.filter((r) => r.success && r.warning);
  const calculatedCount = importResult.successfulRows - warningRows.length;
  const hasIssues = failedRows.length > 0 || warningRows.length > 0;

  return (
    <Card>
      <CardContent className='tw:p-6 tw:text-center'>
        <div
          className={cn(
            'tw:rounded-full tw:inline-flex tw:items-center tw:justify-center tw:mb-4',
            hasIssues ? 'tw:bg-amber-100' : 'tw:bg-emerald-100'
          )}
          style={{ width: 64, height: 64 }}
        >
          {hasIssues
            ? <AlertTriangle className='tw:size-8 tw:text-amber-600' />
            : <Check className='tw:size-8 tw:text-emerald-600' />
          }
        </div>
        <h5 className='tw:font-semibold tw:mb-2'>Import Complete</h5>
        <p className='tw:text-sm tw:text-muted-foreground tw:mb-2'>
          <strong>{importResult.successfulRows}</strong> {importResult.successfulRows === 1 ? 'activity' : 'activities'} created
          {failedRows.length > 0 && (
            <>, <strong className='tw:text-destructive'>{failedRows.length}</strong> failed to import</>
          )}
        </p>

        {importResult.successfulRows > 0 && (
          <div className='tw:flex tw:justify-center tw:gap-4 tw:text-sm tw:mb-4'>
            <span className='tw:text-emerald-700'>
              <Check className='tw:size-3.5 tw:inline tw:mr-1' />
              <strong>{calculatedCount}</strong> calculated
            </span>
            {warningRows.length > 0 && (
              <span className='tw:text-amber-700'>
                <AlertCircle className='tw:size-3.5 tw:inline tw:mr-1' />
                <strong>{warningRows.length}</strong> need{warningRows.length === 1 ? 's' : ''} review
              </span>
            )}
          </div>
        )}

        {!hasIssues && <div className='tw:mb-4' />}

        {failedRows.length > 0 && (
          <div className='tw:rounded-lg tw:border tw:border-red-200 tw:bg-red-50 tw:p-4 tw:mb-4 tw:text-left'>
            <p className='tw:text-sm tw:font-medium tw:text-red-800 tw:mb-2'>Failed rows</p>
            <ul className='tw:text-sm tw:text-red-700 tw:space-y-1 tw:list-disc tw:list-inside tw:mb-0'>
              {failedRows.slice(0, 5).map((r) => (
                <li key={r.index}>Row {r.index + 2}: {r.error || 'Unknown error'}</li>
              ))}
              {failedRows.length > 5 && (
                <li className='tw:text-muted-foreground'>...and {failedRows.length - 5} more</li>
              )}
            </ul>
          </div>
        )}
        {warningRows.length > 0 && (
          <div className='tw:rounded-lg tw:border tw:border-amber-200 tw:bg-amber-50 tw:p-4 tw:mb-4 tw:text-left'>
            <p className='tw:text-sm tw:font-medium tw:text-amber-800 tw:mb-2'>
              {warningRows.length} {warningRows.length === 1 ? 'activity' : 'activities'} saved without emission calculations
            </p>
            <p className='tw:text-sm tw:text-amber-700 tw:mb-3'>
              These activities were created but their emissions could not be calculated.
              Review and correct the activity details (e.g. region, fuel type, or unit) to resolve.
            </p>
            <ul className='tw:text-sm tw:text-amber-700 tw:space-y-1 tw:list-disc tw:list-inside tw:mb-0'>
              {warningRows.slice(0, 5).map((r) => (
                <li key={r.index}>Row {r.index + 2}: {r.warning}</li>
              ))}
              {warningRows.length > 5 && (
                <li className='tw:text-muted-foreground'>...and {warningRows.length - 5} more</li>
              )}
            </ul>
          </div>
        )}
        <div className='tw:flex tw:justify-center tw:gap-3'>
          <Button variant='outline' onClick={onImportMore}>
            Import More
          </Button>
          {warningRows.length > 0 && onViewFailedActivities ? (
            <>
              <Button variant='outline' onClick={onViewActivities}>
                View All Activities
              </Button>
              <Button onClick={onViewFailedActivities}>
                Review Failed Calculations
                <ExternalLink className='tw:size-3.5' />
              </Button>
            </>
          ) : (
            <Button onClick={onViewActivities}>
              View Activities
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
